/**
 * Tasks API Service - Central task data management
 *
 * This service handles all task-related API operations including:
 * - Task CRUD operations (create, read, update, delete)
 * - Task filtering and searching with infinite scroll
 * - Task metadata (types, groups, statuses)
 * - Task updates/comments system
 * - AI-powered task generation
 *
 * Key endpoints:
 * - GET /api/tasks/ - Main task list with filtering
 * - GET /api/matters/{matterId}/tasks/{taskId}/ - Single task details
 * - POST /api/matters/{matterId}/tasks/ - Create new task
 * - PATCH /api/matters/{matterId}/tasks/{taskId}/ - Update task
 *
 * Filter parameters:
 * - search, assignee, status, matter, firm - standard filters
 * - canonical_group, canonical_type - new category-based filters
 * - ordering, limit, offset - pagination and sorting
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import {
  FilterDefinition,
  TASK_FILTERS_CONFIG,
  TaskServiceFilterParams,
} from '@/components/tasks-datatable/config/filterConfig';

import {
  CreateTaskUpdatePayload,
  Task,
  TaskGroup,
  TaskSearchResults,
  TaskStatus,
  TaskType,
  TaskTypeAction,
  TaskUpdate,
  UpdateTaskExternalCMSStatusPayload,
  UpdateTaskPayload,
} from '../types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskType,
  Contact,
} from '../types/task-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import { coverageApiClient } from './coverageService';
import { enhancedApi as taskActivityApi } from './generated/taskActivityService';

/**
 * UI-only parameters that should not affect API cache keys
 *
 * These params are used for client-side UI state (open panels, modals, etc.)
 * and are stripped when generating RTK Query cache keys to prevent unnecessary refetches.
 *
 * Current Usage:
 * - task: Used for deep linking to a task detail (e.g., ?task=123)
 */
const UI_ONLY_PARAMS = ['task', 'task_matter'] as const;

/**
 * Strips UI-only parameters from query args for stable cache key generation
 *
 * @param params - Query parameters that may include UI-only params
 * @returns Clean params object with only data-affecting parameters
 *
 * @example
 * stripUIParams({ limit: 50, task: '123' }) // => { limit: 50 }
 */
function stripUIParams<T extends Record<string, unknown>>(
  params: T,
): Omit<T, (typeof UI_ONLY_PARAMS)[number]> {
  const cleaned = { ...params };
  UI_ONLY_PARAMS.forEach((param) => {
    delete cleaned[param];
  });
  return cleaned as Omit<T, (typeof UI_ONLY_PARAMS)[number]>;
}

interface GroupCount {
  canonical_group: CanonicalTaskGroup;
  count: number;
}

export interface TypeCount {
  group: CanonicalTaskGroup;
  type: CanonicalTaskType;
  count: number;
  id: string;
}

export interface TaskCountsResponse {
  groups: GroupCount[];
  types: TypeCount[];
}

/** Companion task summary for cross-case task tracking */
export interface CompanionTaskSummary {
  matter_id: string;
  matter_name: string;
  is_primary: boolean;
  task_id: string | null; // null if matter has no matching task
  task_name: string | null; // null if matter has no matching task
  task_status: string | null;
  task_due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
}

export interface CompanionTasksResponse {
  companion_tasks: CompanionTaskSummary[];
}

export interface TaskMetricCount {
  category: string;
  count: number;
}

export interface TaskFilterOption {
  label: string;
  value: string;
}

export interface TaskFilterOptions {
  firms?: TaskFilterOption[];
  incident_types?: TaskFilterOption[];
  matter_statuses?: TaskFilterOption[];
  priorities?: TaskFilterOption[];
  matters?: TaskFilterOption[];
  coverage_carriers?: TaskFilterOption[];
  coverages?: TaskFilterOption[]; // Legacy support if needed, but backend sends coverage_carriers
  assignees?: TaskFilterOption[];
  last_update_ranges?: TaskFilterOption[];
  last_update_actors?: TaskFilterOption[];
  task_statuses?: TaskFilterOption[];
  police_departments?: TaskFilterOption[];
  medical_providers?: TaskFilterOption[];
  created_date_ranges?: TaskFilterOption[];
  task_sources?: TaskFilterOption[];
  field_change_reasons?: TaskFilterOption[];
  agent_statuses?: TaskFilterOption[];
}

export interface TaskListMetadata {
  static_metrics: TaskMetricCount[];
  static_groups_and_types?: TaskCountsResponse;
  filter_options?: TaskFilterOptions;
}

export interface GenerateTaskPayload {
  description: string;
}

/**
 * Skip reasons returned by the bulk update endpoint.
 * Each reason indicates why a specific task was not updated.
 */
export type BulkUpdateSkipReason =
  | 'task_not_found'
  | 'no_managing_paralegal'
  | 'no_case_manager'
  | 'invalid_assignee_id'
  | 'invalid_assignee_value'
  | 'unassign_not_allowed'
  | 'trr_attachments_required';

/**
 * Special assignee values that the backend resolves per-task.
 * Use these constants instead of magic strings.
 */
export const SPECIAL_ASSIGNEE_VALUES = {
  /** Resolves to task.matter.firm.managing_paralegal_id */
  MANAGING_PARALEGAL: 'managing_paralegal',
  /** Resolves to task.matter.case_manager_id */
  CASE_MANAGER: 'case_manager',
} as const;

export type SpecialAssigneeValue =
  (typeof SPECIAL_ASSIGNEE_VALUES)[keyof typeof SPECIAL_ASSIGNEE_VALUES];

export interface BulkUpdateSkippedTask {
  task_id: string;
  reason: BulkUpdateSkipReason;
}

/**
 * Request payload for bulk task updates.
 * Only provided fields are updated; null/omitted = no change.
 */
export interface BulkUpdateTasksRequest {
  task_ids: string[];
  /** User UUID, "managing_paralegal", "case_manager", or null for no change */
  assignee?: string | null;
  status_id?: string | null;
  due_date?: string | null;
  sla_resolution_target?: string | null;
  /** Comment added to each updated task for audit trail */
  change_reason?: string | null;
}

/**
 * Response from bulk task update endpoint.
 * Partial success is possible - check skipped array for failures.
 */
export interface BulkUpdateTasksResponse {
  updated_count: number;
  skipped: BulkUpdateSkippedTask[];
}

/**
 * Response from the LOR discovery status endpoint.
 * Represents the current state of the AI-powered LOR field discovery workflow.
 */
export interface LorDiscoveryStatusResponse {
  status:
    | 'not_started'
    | 'pending'
    | 'preparing'
    | 'discovering'
    | 'drafting'
    | 'ready'
    | 'failed';
  status_description?: string;
  is_running: boolean;
  error_message?: string | null;
  discovery_result?: {
    fields_verified: number;
    fields_corrected: number;
    fields_missing: number;
    summary?: string;
  } | null;
}

export interface Verify1PCoverageRequest {
  decisions: Array<{ coverage_id: string; decision: 'YES' | 'NO' }>;
}

export type TaskFilter = TaskServiceFilterParams;

/**
 * Helper to build URLSearchParams from TaskFilter
 * Handles array serialization for multi-value filters since URLSearchParams
 * doesn't automatically handle arrays in the way Django expects (repeated keys).
 *
 * FULLY CONFIGURATION DRIVEN:
 * Iterates through TASK_FILTERS_CONFIG to serialize all parameters.
 */
function buildTaskQueryString(filter: TaskFilter): string {
  const params = new URLSearchParams();

  (Object.entries(TASK_FILTERS_CONFIG) as [string, FilterDefinition][]).forEach(
    ([, config]) => {
      // The service key (e.g. 'statuses') is what we look for in the 'filter' object
      const serviceKey = config.serviceKey as keyof TaskFilter;
      // The URL key (e.g. 'status') is what we send to the API
      const apiKey = config.urlKey || config.serviceKey;

      const value = filter[serviceKey];

      if (value === undefined || value === null) {
        return;
      }

      if (config.isArray) {
        if (Array.isArray(value)) {
          (value as string[]).forEach((item) => params.append(apiKey, item));
        } else {
          // Handle single string for array param (robustness)
          params.append(apiKey, value.toString());
        }
      } else {
        // Single value handling
        params.set(apiKey, value.toString());
      }
    },
  );

  return params.toString();
}

export const tasksApiClient = createApi({
  reducerPath: 'tasksApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'Tasks',
    'TaskUpdates',
    'TaskTypes',
    'TaskGroups',
    'TaskStatuses',
    'TaskCounts',
    'TaskListMetadata',
    'TaskInstructions',
  ],
  endpoints: (builder) => ({
    // Task List endpoint
    getTaskTypes: builder.query<TaskType[], void>({
      query: () => `api/tasks/types/`,
      providesTags: ['TaskTypes'],
    }),
    getTaskGroups: builder.query<TaskGroup[], void>({
      query: () => `api/tasks/groups/`,
      providesTags: ['TaskGroups'],
    }),
    getTaskStatuses: builder.query<TaskStatus[], void>({
      query: () => `api/tasks/statuses/`,
      providesTags: ['TaskStatuses'],
    }),
    getTaskCounts: builder.query<
      TaskCountsResponse,
      Partial<TaskFilter> | void
    >({
      query: (filter) => {
        if (!filter) return `api/task-counts/`;
        const queryString = buildTaskQueryString(filter);
        return queryString
          ? `api/task-counts/?${queryString}`
          : `api/task-counts/`;
      },
      // Custom cache key generation: Ignore UI-only params (like 'task=123' for side panel)
      // so opening a task doesn't trigger a refetch of the main list.
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        if (!queryArgs) return endpointName;
        const dataParams = stripUIParams(queryArgs);
        return `${endpointName}(${JSON.stringify(dataParams)})`;
      },
      transformResponse: (response: { data: TaskCountsResponse }) =>
        response.data,
      providesTags: ['TaskCounts'],
    }),
    getTaskListMetadata: builder.query<TaskListMetadata, TaskFilter>({
      query: (filter) => {
        const queryString = buildTaskQueryString(filter);
        return `api/task-list-metadata/${queryString ? `?${queryString}` : ''}`;
      },
      // Custom cache key generation: Ignore UI-only params (like 'task=123' for side panel)
      // so opening a task doesn't trigger a refetch of the main list.
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const dataParams = stripUIParams(queryArgs as Record<string, unknown>);
        return `${endpointName}(${JSON.stringify(dataParams)})`;
      },
      providesTags: (result, error, filter) => [
        {
          type: 'TaskListMetadata',
          id: JSON.stringify(filter),
        },
        'TaskListMetadata',
      ],
    }),
    getTask: builder.query<Task, { matterId: string; taskId: string }>({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'Tasks', id: taskId },
      ],
    }),
    getTaskById: builder.query<Task | undefined, { taskId: string }>({
      query: ({ taskId }) => `api/tasks/${taskId}/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'Tasks', id: taskId },
      ],
    }),
    // Fetch tasks of the same type from companion cases
    getTaskCompanionTasks: builder.query<
      CompanionTasksResponse,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/companion-tasks/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'Tasks', id: `companion-${taskId}` },
      ],
    }),
    getTasks: builder.query<TaskSearchResults, TaskFilter>({
      query: (filter) => {
        const queryString = buildTaskQueryString(filter);
        return {
          url: `api/tasks/?${queryString}`,
        };
      },
      // Custom cache key generation: Ignore UI-only params (like 'task=123' for side panel)
      // so opening a task doesn't trigger a refetch of the main list.
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Strip UI-only params before generating cache key
        const dataParams = stripUIParams(queryArgs as Record<string, unknown>);
        // Generate stable cache key from data params only
        return `${endpointName}(${JSON.stringify(dataParams)})`;
      },
      providesTags: (result, error, filter) => [
        {
          type: 'Tasks',
          id: JSON.stringify({ ...filter, offset: undefined }),
        },
        { type: 'Tasks' },
      ],
    }),

    createTask: builder.mutation<
      Task,
      { matterId: string; task: UpdateTaskPayload; forceCreate?: boolean }
    >({
      query: ({ matterId, task, forceCreate }) => ({
        url: forceCreate
          ? `api/matters/${matterId}/tasks/?force_create=true`
          : `api/matters/${matterId}/tasks/`,
        method: 'POST',
        body: task,
      }),
      invalidatesTags: ['Tasks', 'TaskCounts'],
    }),

    // Ensure initial tasks for a matter
    ensureInitialTasks: builder.mutation<void, { matterId: string }>({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/tasks/ensure-initial/`,
        method: 'POST',
      }),
      invalidatesTags: ['Tasks', 'TaskCounts'],
    }),

    updateTask: builder.mutation<
      Task,
      { matterId: string; taskId: string; task: UpdateTaskPayload }
    >({
      query: ({ matterId, taskId, task }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/`,
        method: 'PATCH',
        body: task,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        'Tasks',
        'TaskCounts',
        // Invalidate task updates when change_reason is provided (field change creates an update)
        { type: 'TaskUpdates', id: taskId },
      ],
      // Cross-slice invalidation for task activity (domain events)
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        // Invalidate task activity cache to refetch domain events
        dispatch(taskActivityApi.util.invalidateTags(['tasks']));
      },
    }),

    /**
     * Bulk update multiple tasks in a single request.
     * Supports updating assignee, status, due_date, and sla_resolution_target.
     *
     * Features:
     * - Cross-matter support: Tasks from different matters/firms in single request
     * - Partial success: Some tasks may be skipped (check response.skipped)
     * - Audit trail: change_reason creates a task comment for each updated task
     * - Managing paralegal resolution: Use assignee="managing_paralegal" to auto-resolve
     *
     * @see BulkUpdateTasksRequest for request format
     * @see BulkUpdateTasksResponse for response format
     */
    bulkUpdateTasks: builder.mutation<
      BulkUpdateTasksResponse,
      BulkUpdateTasksRequest
    >({
      query: (payload) => ({
        url: 'api/tasks/bulk-update/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result) => {
        if (!result || result.updated_count === 0) {
          return [];
        }
        return ['Tasks', 'TaskCounts', 'TaskUpdates'];
      },
      // Cross-slice invalidation for task activity (domain events)
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.updated_count > 0) {
            // Invalidate task activity cache to refetch domain events
            dispatch(taskActivityApi.util.invalidateTags(['tasks']));
          }
        } catch {
          // Query failed, no need to invalidate
        }
      },
    }),

    updateTaskExternalCMSStatus: builder.mutation<
      Task,
      {
        matterId: string;
        taskId: string;
        data: UpdateTaskExternalCMSStatusPayload;
      }
    >({
      query: ({ matterId, taskId, data }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/update-external-cms-status/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Tasks', id: taskId },
        'Tasks',
        'TaskCounts',
      ],
    }),
    deleteTask: builder.mutation<void, { matterId: string; taskId: string }>({
      query: ({ matterId, taskId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tasks', 'TaskCounts'],
    }),

    // Task Update endpoints
    getTaskUpdates: builder.query<
      TaskUpdate[],
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/updates/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'TaskUpdates', id: taskId },
      ],
    }),

    createTaskUpdate: builder.mutation<
      TaskUpdate,
      { matterId: string; taskId: string; update: CreateTaskUpdatePayload }
    >({
      query: ({ matterId, taskId, update }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/updates/`,
        method: 'POST',
        body: update,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskUpdates', id: taskId },
        { type: 'Tasks' },
        'TaskCounts',
        'TaskListMetadata',
      ],
    }),

    updateTaskUpdate: builder.mutation<
      TaskUpdate,
      {
        matterId: string;
        taskId: string;
        updateId: string;
        update: CreateTaskUpdatePayload;
      }
    >({
      query: ({ matterId, taskId, updateId, update }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/updates/${updateId}/`,
        method: 'PATCH',
        body: update,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskUpdates', id: taskId },
      ],
    }),

    deleteTaskUpdate: builder.mutation<
      void,
      { matterId: string; taskId: string; updateId: string }
    >({
      query: ({ matterId, taskId, updateId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/updates/${updateId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskUpdates', id: taskId },
      ],
    }),

    // TODO(dean): Point this at actions endpoint
    // n.b. not sure if this should take in a taskType or a taskId
    getTaskTypeActions: builder.query<TaskTypeAction[], string>({
      query: (taskType) => `api/task-type-actions/${taskType}/`,
    }),

    // TODO(dean): Point this at providers endpoint
    getProviders: builder.query<Contact[], void>({
      query: () => `api/providers/`,
    }),

    /**
     * Get the status of the LOR auto-discovery workflow for a task.
     * Returns the current state of the AI discovery process.
     */
    getLorDiscoveryStatus: builder.query<
      LorDiscoveryStatusResponse,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/lor-discovery-status/`,
    }),

    verifyFirstPartyCoverage: builder.mutation<
      Task,
      { matterId: string; taskId: string; body: Verify1PCoverageRequest }
    >({
      query: ({ matterId, taskId, body }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/verify-1p-coverage/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        'Tasks',
        'TaskCounts',
        { type: 'TaskUpdates', id: taskId },
      ],
      onQueryStarted: async ({ matterId }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            coverageApiClient.util.invalidateTags([
              { type: 'Coverages', id: matterId },
            ]),
          );
          dispatch(taskActivityApi.util.invalidateTags(['tasks']));
        } catch {
          /* no-op */
        }
      },
    }),
    // Task Instructions
    getTaskInstruction: builder.query<
      TaskInstructionResponse,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) =>
        `api/matters/${matterId}/tasks/${taskId}/instructions/`,
      providesTags: (result, error, { taskId }) => [
        { type: 'TaskInstructions' as const, id: taskId },
      ],
    }),
    generateTaskInstruction: builder.mutation<
      TaskInstructionResponse,
      { matterId: string; taskId: string }
    >({
      query: ({ matterId, taskId }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/instructions/generate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskInstructions' as const, id: taskId },
      ],
    }),
    submitTaskInstructionFeedback: builder.mutation<
      TaskInstructionResponse,
      { matterId: string; taskId: string; reason?: string }
    >({
      query: ({ matterId, taskId, reason }) => ({
        url: `api/matters/${matterId}/tasks/${taskId}/instructions/feedback/`,
        method: 'POST',
        body: reason ? { reason } : {},
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskInstructions' as const, id: taskId },
      ],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetTaskQuery,
  useGetTaskByIdQuery,
  useLazyGetTaskByIdQuery,
  useGetTaskCompanionTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useBulkUpdateTasksMutation,
  useUpdateTaskExternalCMSStatusMutation,
  useDeleteTaskMutation,
  useGetTaskUpdatesQuery,
  useGetTaskTypeActionsQuery,
  useGetProvidersQuery,
  useCreateTaskUpdateMutation,
  useUpdateTaskUpdateMutation,
  useDeleteTaskUpdateMutation,
  useGetTaskStatusesQuery,
  useGetTaskTypesQuery,
  useGetTaskGroupsQuery,
  useGetTaskCountsQuery,
  useGetTaskListMetadataQuery,
  useEnsureInitialTasksMutation,
  useGetLorDiscoveryStatusQuery,
  useVerifyFirstPartyCoverageMutation,
  useGetTaskInstructionQuery,
  useGenerateTaskInstructionMutation,
  useSubmitTaskInstructionFeedbackMutation,
} = tasksApiClient;

export type { TaskType } from '../types/client-intake-types';

export interface TaskInstructionResponse {
  id: string;
  task_id: string;
  matter_id: string;
  is_latest: boolean;
  recap: string | null;
  next_actions: string[];
  reasoning: string;
  confidence: number;
  context_sources_used: string[];
  model_used: string;
  generation_mode: string;
  generation_time_seconds: number | null;
  thumbs_down: boolean;
  thumbs_down_reason: string | null;
  created_at: string;
}
